<script setup>
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useRouter } from 'vue-router';

import { api } from '@/api/client.js';
import HotspotRow from '@/components/panels/HotspotRow.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { RAGE_CLICK_EMPTY } from '@/utils/emptyStateCopy.js';

const tenant = useTenantStore();
const router = useRouter();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const rows = ref([]);

// Story 6.14 AC1 — clicking a hotspot row navigates to the heatmap
// for that page URL with the active time window preserved via query
// param (Pinia state is the source of truth; the URL is for shareability
// + Back navigation continuity).
function navigateToHeatmap(row) {
  if (!row.pageUrlHash) return; // safety — Stories 6.8/6.14 emit this
  router.push({
    name: 'heatmap',
    params: { pageUrlHash: row.pageUrlHash },
    query: { window: timeWindow.value },
  });
}

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.rageHotspots({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    rows.value = result.rows ?? [];
    ps.setState(rows.value.length === 0 ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load rage hotspots",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });
</script>

<template>
  <PanelCard
    title="Rage clicks"
    help-key="rageClicks"
    :state="ps.state.value"
    :empty-headline="RAGE_CLICK_EMPTY.headline"
    :empty-body="RAGE_CLICK_EMPTY.body ?? ''"
    :empty-action="RAGE_CLICK_EMPTY.action"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <ul class="hotspot-list">
      <li
        v-for="row in rows"
        :key="`${row.pageUrl}|${row.selector}`"
      >
        <HotspotRow
          :page-url="row.pageUrl"
          :selector="row.selector"
          :count="row.count"
          :spike-onset="row.spikeOnset"
          :sparkline-points="row.sparkline"
          variant="rage"
          :can-drill-down="!!row.pageUrlHash"
          :on-click="() => navigateToHeatmap(row)"
        />
      </li>
    </ul>
    <template
      v-if="rows.length"
      #tableFallback
    >
      <table class="hotspot-table">
        <caption class="visually-hidden">
          Rage-click hotspots
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Page URL
            </th>
            <th scope="col">
              DOM Selector
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Count
            </th>
            <th scope="col">
              Spike Onset
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="`tbl-${row.pageUrl}|${row.selector}`"
          >
            <td>{{ row.pageUrl }}</td>
            <td><code>{{ row.selector }}</code></td>
            <td class="numeric">
              {{ row.count }}
            </td>
            <td>{{ row.spikeOnset?.label ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.hotspot-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.hotspot-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.hotspot-table th,
.hotspot-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.hotspot-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.hotspot-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}

.hotspot-table code {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
}
</style>
